import { useId } from "react";
import type { Ref, SVGProps } from "react";

type AppLogoIconProps = Omit<SVGProps<SVGSVGElement>, "children"> & {
  /**
   * Accessible title. If omitted, the SVG is treated as decorative.
   */
  title?: string;
  ref?: Ref<SVGSVGElement>;
};

export const AppLogoIcon = ({
  title,
  className,
  ref: svgRef,
  ...props
}: Readonly<AppLogoIconProps>) => {
  const titleId = useId();
  const hasTitle = title !== undefined && title !== "";

  const ariaProps: SVGProps<SVGSVGElement> = hasTitle
    ? { "aria-labelledby": titleId, role: "img" }
    : { "aria-hidden": true };

  return (
    <svg
      ref={svgRef}
      // Tight, square viewBox so it scales nicely at small sizes.
      viewBox="228 221.5 569 569"
      // Defaults; Tailwind width/height utilities can override.
      width="1em"
      height="1em"
      // Enables Tailwind `text-*` (via currentColor) and also `fill-*` overrides.
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      className={className}
      {...ariaProps}
      {...props}
    >
      {hasTitle ? <title id={titleId}>{title}</title> : null}

      <g shapeRendering="geometricPrecision">
        <path
          data-part="bar-1"
          d="M254.25 626.30V266.07L289.16 237.50H379.27L346.50 264.32V550.81Z"
        />
        <path
          data-part="bar-2"
          d="M399.25 510.12V267.57L434.16 239.00H527.89L493.50 267.14V432.99Z"
        />
        <path data-part="bar-3" d="M546.25 395.12V268.98L582.27 239.50H673.25V291.19Z" />
        <path
          data-part="base"
          d="M692.17 774.50H679.00V426.09L628.75 467.21V773.75H537.25V537.61L488.75 577.30V773.75H396.25V654.29L346.25 695.21V774.50H254.25V700.38L770.75 277.71V710.20Z"
        />
      </g>
    </svg>
  );
};

AppLogoIcon.displayName = "LogoMark";
